import { Grid2 as Grid } from '@mui/material';
import React, { useMemo } from 'react';
import { Dropdown, useInputHelper } from 'framework/form';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import { CYCLE_DAYS, CYCLE_DAYS_LABELS, CYCLE_FREQUENCY, CycleDay } from 'domain/shared/newProject';
import { demoMap, weeklyCycleMap } from './baseCycleSettingsUtills';

interface EditBaseCycleSettingsProps {
  isDisabled?: boolean;
  source: string;
  cycleType?: string;
}

const EditBaseCycleSettings: React.FC<EditBaseCycleSettingsProps> = ({
  isDisabled = false,
  source,
  cycleType = CYCLE_FREQUENCY.WEEKLY,
}) => {
  const { getValue, getRawOnChangeCallback } = useInputHelper({ source });
  const values = getValue();
  const rawOnChangeCallback = getRawOnChangeCallback();

  const cycleCalendarAvailability = useMemo(() => {
    const currentStartDate = values.cycleStartDay;
    const currentEndDate = values.cycleEndDay;
    const daysForEnding = weeklyCycleMap[currentStartDate] || [];
    const daysForDemo = demoMap[currentEndDate] || [];

    return Object.keys(CYCLE_DAYS).map(key => ({
      value: key,
      label: CYCLE_DAYS_LABELS[key],
      disabledForEnding: !daysForEnding.includes(key as CycleDay),
      disabledForPlanning: false,
      disabledForDemo: !daysForDemo.includes(key as CycleDay),
    }));
  }, [values.cycleEndDay, values.cycleStartDay]);

  const onStartDayChangeCallback = (dropdownValues: FormFieldsData) => {
    const newCycleStartDay = dropdownValues[`${source}.cycleStartDay`];

    rawOnChangeCallback?.({
      [`${source}.cycleStartDay`]: newCycleStartDay,
      [`${source}.cycleEndDay`]: weeklyCycleMap[newCycleStartDay]?.[0] || null,
      [`${source}.planningDay`]: null,
      [`${source}.demoDay`]: null,
    });
  };

  return (
    <Grid container spacing={2} size={12}>
      <Grid size={2}>
        <Dropdown
          source={`${source}.cycleStartDay`}
          placeholder={'Start date'}
          label="Cycle start Day"
          disabled={isDisabled}
          onChangeCallback={onStartDayChangeCallback}
          options={cycleCalendarAvailability}
        />
      </Grid>
      <Grid size={2}>
        <Dropdown
          source={`${source}.cycleEndDay`}
          placeholder={'End date'}
          label="Cycle end Day"
          disabled={isDisabled}
          value={values.cycleEndDay}
          options={cycleCalendarAvailability.map(v => ({ ...v, disabled: v.disabledForEnding }))}
        />
      </Grid>
      <Grid size={2}>
        <Dropdown
          source={`${source}.planningDay`}
          placeholder={'Planning day'}
          label="Planning day"
          disabled={isDisabled}
          options={cycleCalendarAvailability.map(v => ({ ...v, disabled: v.disabledForPlanning }))}
        />
      </Grid>
      <Grid size={2}>
        <Dropdown
          source={`${source}.demoDay`}
          placeholder={'Demo day'}
          label="Demo Day"
          disabled={isDisabled}
          options={cycleCalendarAvailability.map(v => ({ ...v, disabled: v.disabledForDemo }))}
        />
      </Grid>
    </Grid>
  );
};

export default EditBaseCycleSettings;
